<script lang="ts">
import {computed, defineComponent, onMounted, ref} from "vue";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {useAuthStore} from "~/plugins/store/auth";
import {reloadNuxtApp} from "nuxt/app";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";

export default defineComponent({
  components: {LoadingDialog},
  setup() {
    const drawer = ref(true);
    const rail = ref(true);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = computed(() => authStore.getUser());
    const activePanels = ref([0]);
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const items = ref([
      { text: 'Date de identificare', icon: 'mdi-account', path: '/account/profile' },
      { text: 'Masinile mele', icon: 'mdi-car', path: '/account/vehicles' },
      { text: 'Securitate', icon: 'mdi-lock', path: '/account/security' },
      { text: '2FA', icon: 'mdi-two-factor-authentication', path: '/account/2fa' },
      { text: 'Preferinte', icon: 'mdi-cogs', path: '/account/preferences' },
      { text: 'Istoric comenzi', icon: 'mdi-history', path: '/account/orders' },
    ]);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      handleResize(); // Set initial width
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    //We need the width of the screen in order to change the account button style
    const width = ref(0);
    const isExpandable = computed(() => width.value < 960);

    const handleResize = () => {
      if (typeof window !== 'undefined') {
        width.value = window.innerWidth;
      }
      if (!isExpandable.value) {
        activePanels.value = [0]; // Asigură că panoul rămâne deschis
      } else {
        activePanels.value = []; // Închide panoul când lățimea ecranului este mai mică de 960
      }
    };

    const logout = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = 'Se deconectează...';
      await authStore.logout();
      reloadNuxtApp();
    };

    return {
      drawer,
      rail,
      translationList,
      translation,
      user,
      items,
      activePanels,
      logout,
      isExpandable,
      showLoadingDialog,
      messageLoadingDialog,
    }
  }
});
</script>

<template>
  <v-card rounded="xl" variant="elevated" elevation="15" v-if="user" class="w-100">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-expansion-panels eager color="surface" rounded="xl" variant="accordion"
                        :readonly="!isExpandable"
                        :model-value="isExpandable ? activePanels : [0]">
      <v-expansion-panel v-model="activePanels" :hide-actions="!isExpandable" class="w-100">
        <v-expansion-panel-title class="">
            <v-list-item
                class="py-0 px-0"
                :subtitle="user.email"
                :title="user.full_name"
            >
              <template #prepend>
                <v-avatar color="primary" size="large">
                  <span class="text-h5" v-if="user.initials">{{ user.initials }}</span>
                  <span class="text-h5" v-else><v-icon>mdi-account-circle</v-icon></span>
                </v-avatar>
              </template>
            </v-list-item>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="surface">
          <v-divider></v-divider>
          <v-list
              lines="one"
              density="comfortable"
              nav
              bg-color="surface"
          >
            <template v-for="(item, index) in items" :key="index">
              <NuxtLink :to="item.path" class="text-decoration-none color-contrast" :aria-label="item.text">
                <v-list-item
                    :value="item"
                    color="primary"
                    exact :active="$route.path.startsWith(item.path)" link
                >
                  <template v-slot:prepend>
                    <v-icon :icon="item.icon"></v-icon>
                  </template>

                  <v-list-item-title v-text="item.text" class="fs-16"></v-list-item-title>
                </v-list-item>
              </NuxtLink>
            </template>
          </v-list>

          <v-divider></v-divider>

          <v-list lines="one"
                  density="comfortable"
                  bg-color="surface"
                  base-color="error"
                  nav>
            <v-list-item
                color="red"
                @click="logout"
                class="cursor-pointer"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-logout"></v-icon>
              </template>

              <v-list-item-title class="fs-16">
                {{ translation(translationList.logout, 'Deconectare') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<style scoped>
.v-expansion-panel-title__icon {
  display: none !important;
}

</style>
